import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, Search, Loader2, Edit, Trash2,
  KeyRound, Building2, MoreHorizontal, Activity,
  CheckCircle2, XCircle, ShieldAlert, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SubscriptionInfo {
  plan: string | null;
  tier: string | null;
  status: string | null;
  period: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  has_stripe_customer: boolean;
  has_stripe_subscription: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

interface User {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  org_id: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  invited_at: string | null;
  has_password: boolean;
  providers: string[];
  oauth_providers?: string[];
  is_banned: boolean;
  org?: { id: string; name: string } | null;
  subscription?: SubscriptionInfo | null;
  roles?: { role: string; org_id: string | null }[];
}

interface Organization { id: string; name: string; }

export function UserManagementPanel() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activity, setActivity] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: "", email: "", phone: "", org_id: "",
    role: "owner" as "owner" | "admin" | "manager" | "rep" | "client",
  });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!searchQuery.trim()) { setFilteredUsers(users); return; }
    const q = searchQuery.toLowerCase();
    setFilteredUsers(users.filter((u) =>
      u.email?.toLowerCase().includes(q) ||
      (u.full_name?.toLowerCase() || "").includes(q) ||
      (u.org?.name?.toLowerCase() || "").includes(q) ||
      u.roles?.some((r) => r.role.toLowerCase().includes(q))
    ));
  }, [searchQuery, users]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, orgsRes] = await Promise.all([
        supabase.functions.invoke("platform-admin-list-users"),
        supabase.from("organizations").select("id, name").order("name"),
      ]);

      if (usersRes.error) throw usersRes.error;
      if (usersRes.data?.error) throw new Error(usersRes.data.error);

      setUsers(usersRes.data?.users || []);
      setFilteredUsers(usersRes.data?.users || []);
      setOrganizations(orgsRes.data || []);
    } catch (e: any) {
      console.error("Failed to load users:", e);
      toast.error(`Failed to load users: ${e.message}`);
    } finally { setLoading(false); }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    const orgRole = user.roles?.find((r) => r.org_id === user.org_id);
    setEditFormData({
      full_name: user.full_name || "",
      email: user.email,
      phone: user.phone || "",
      org_id: user.org_id || "",
      role: (orgRole?.role as any) || "owner",
    });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    try {
      setSaving(true);
      const { error: profileError } = await supabase.from("profiles").update({
        full_name: editFormData.full_name,
        email: editFormData.email,
        phone: editFormData.phone || null,
        org_id: editFormData.org_id || null,
      }).eq("id", selectedUser.id);
      if (profileError) throw profileError;

      if (editFormData.org_id) {
        await supabase.from("user_roles").delete()
          .eq("user_id", selectedUser.id).eq("org_id", editFormData.org_id);
        await supabase.from("user_roles").insert({
          user_id: selectedUser.id, org_id: editFormData.org_id, role: editFormData.role as any,
        });
      }
      toast.success("User updated");
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (e: any) {
      toast.error("Failed to update user");
    } finally { setSaving(false); }
  };

  const handleResetPassword = async (user: User) => {
    if (!confirm(`Send password reset email to ${user.email}?`)) return;
    try {
      const { data, error } = await supabase.functions.invoke("admin-reset-password", {
        body: { userEmail: user.email },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Reset email sent to ${user.email}`);
    } catch (e: any) {
      toast.error(`Failed: ${e.message}`);
    }
  };

  const handleRemoveFromOrg = async (user: User) => {
    if (!user.org_id) return;
    if (!confirm(`Remove ${user.full_name || user.email} from their org?`)) return;
    try {
      await supabase.from("profiles").update({ org_id: null }).eq("id", user.id);
      await supabase.from("user_roles").delete().eq("user_id", user.id).eq("org_id", user.org_id);
      toast.success("Removed from organization");
      loadData();
    } catch { toast.error("Failed to remove user"); }
  };

  const handleViewActivity = async (user: User) => {
    setSelectedUser(user);
    setActivityOpen(true);
    setActivityLoading(true);
    setActivity(null);
    try {
      const { data, error } = await supabase.functions.invoke("platform-admin-user-activity", {
        body: { userId: user.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setUsers((prev) => prev.map((existing) =>
        existing.id === user.id
          ? {
              ...existing,
              has_password: !!data?.auth?.has_password,
              providers: Array.isArray(data?.auth?.providers) ? data.auth.providers : existing.providers,
              oauth_providers: Array.isArray(data?.auth?.providers)
                ? data.auth.providers.filter((provider: string) => provider !== "email")
                : existing.oauth_providers,
              last_sign_in_at: data?.auth?.last_sign_in_at ?? existing.last_sign_in_at,
              email_confirmed_at: data?.auth?.email_confirmed_at ?? existing.email_confirmed_at,
              is_banned: !!data?.auth?.is_banned,
            }
          : existing,
      ));
      setFilteredUsers((prev) => prev.map((existing) =>
        existing.id === user.id
          ? {
              ...existing,
              has_password: !!data?.auth?.has_password,
              providers: Array.isArray(data?.auth?.providers) ? data.auth.providers : existing.providers,
              oauth_providers: Array.isArray(data?.auth?.providers)
                ? data.auth.providers.filter((provider: string) => provider !== "email")
                : existing.oauth_providers,
              last_sign_in_at: data?.auth?.last_sign_in_at ?? existing.last_sign_in_at,
              email_confirmed_at: data?.auth?.email_confirmed_at ?? existing.email_confirmed_at,
              is_banned: !!data?.auth?.is_banned,
            }
          : existing,
      ));
      setActivity(data);
    } catch (e: any) {
      toast.error(`Failed to load activity: ${e.message}`);
    } finally { setActivityLoading(false); }
  };

  const getRoleBadge = (roles: User["roles"]) => {
    if (!roles || roles.length === 0) return <Badge variant="outline">No role</Badge>;
    const platformOwner = roles.find((r) => r.role === "owner" && r.org_id === null);
    if (platformOwner) return <Badge className="bg-purple-600">Platform Owner</Badge>;
    const orgRoles = roles.filter((r) => r.org_id !== null);
    if (orgRoles.length === 0) return <Badge variant="outline">No role</Badge>;
    return (
      <div className="flex flex-wrap gap-1">
        {orgRoles.map((r, i) => <Badge key={i} variant="secondary">{r.role}</Badge>)}
      </div>
    );
  };

  const fmtRelative = (iso: string | null) =>
    iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : "Never";

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" /> All Platform Users
            </CardTitle>
            <CardDescription>
              {users.length} total · {users.filter(u => u.last_sign_in_at).length} have logged in · {users.filter(u => !u.email_confirmed_at).length} unverified
            </CardDescription>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by name, email, org, or role..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>
          {searchQuery && <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")}>Clear</Button>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Auth Status</TableHead>
                <TableHead>Last Sign In</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    {searchQuery ? `No users found matching "${searchQuery}"` : "No users found"}
                  </TableCell>
                </TableRow>
              ) : filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{user.full_name || "N/A"}</span>
                      <span className="text-sm text-muted-foreground">{user.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.org ? (
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{user.org.name}</span>
                      </div>
                    ) : <span className="text-muted-foreground text-sm">No org</span>}
                  </TableCell>
                  <TableCell>
                    {user.subscription ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge
                            className={
                              user.subscription.status === "active" ? "bg-green-600" :
                              user.subscription.status === "trial" || user.subscription.status === "trialing" ? "bg-blue-600" :
                              user.subscription.status === "past_due" ? "bg-amber-600" :
                              user.subscription.status === "canceled" ? "bg-red-600" :
                              "bg-muted text-foreground"
                            }
                          >
                            {user.subscription.status || "—"}
                          </Badge>
                          {user.subscription.tier && (
                            <Badge variant="outline" className="text-[10px]">{user.subscription.tier}</Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {user.subscription.has_stripe_subscription ? "Stripe ✓" : user.subscription.has_stripe_customer ? "Customer only" : "No Stripe"}
                          {user.subscription.period ? ` · ${user.subscription.period}` : ""}
                        </div>
                        {user.subscription.current_period_end && (
                          <div className="text-[11px] text-muted-foreground">
                            Renews {format(new Date(user.subscription.current_period_end), "MMM d")}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-muted-foreground text-sm">—</span>}
                  </TableCell>
                  <TableCell>{getRoleBadge(user.roles)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-xs">
                        {user.email_confirmed_at ? (
                          <><CheckCircle2 className="w-3 h-3 text-green-600" /> Verified</>
                        ) : (
                          <><XCircle className="w-3 h-3 text-amber-600" /> Unverified</>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        {user.has_password ? (
                          <><ShieldCheck className="w-3 h-3 text-green-600" /> Password set</>
                        ) : (
                          <><ShieldAlert className="w-3 h-3 text-muted-foreground" /> No password</>
                        )}
                      </div>
                      {(user.oauth_providers || user.providers.filter(p => p !== "email")).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {(user.oauth_providers || user.providers.filter(p => p !== "email")).map((p) => (
                            <Badge key={p} variant="outline" className="text-[10px] px-1 py-0">{p}</Badge>
                          ))}
                        </div>
                      )}
                      {user.is_banned && <Badge variant="destructive" className="text-[10px]">Banned</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className={`text-sm ${user.last_sign_in_at ? "" : "text-amber-600 font-medium"}`}>
                        {fmtRelative(user.last_sign_in_at)}
                      </span>
                      {user.last_sign_in_at && (
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(user.last_sign_in_at), "MMM d, yyyy h:mm a")}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(user.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewActivity(user)}>
                          <Activity className="w-4 h-4 mr-2" /> View Activity
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditUser(user)}>
                          <Edit className="w-4 h-4 mr-2" /> Edit User
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                          <KeyRound className="w-4 h-4 mr-2" /> Send Password Reset
                        </DropdownMenuItem>
                        {user.org_id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleRemoveFromOrg(user)} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Remove from Org
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Activity Dialog */}
        <Dialog open={activityOpen} onOpenChange={setActivityOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedUser?.full_name || selectedUser?.email}</DialogTitle>
              <DialogDescription>{selectedUser?.email}</DialogDescription>
            </DialogHeader>
            {activityLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : activity ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="border rounded-md p-3">
                    <div className="text-xs text-muted-foreground">Last Sign In</div>
                    <div className="font-medium">{fmtRelative(activity.auth.last_sign_in_at)}</div>
                  </div>
                  <div className="border rounded-md p-3">
                    <div className="text-xs text-muted-foreground">Email Verified</div>
                    <div className="font-medium">{activity.auth.email_confirmed_at ? format(new Date(activity.auth.email_confirmed_at), "MMM d, yyyy") : "Not verified"}</div>
                  </div>
                  <div className="border rounded-md p-3">
                    <div className="text-xs text-muted-foreground">Password Set</div>
                    <div className="font-medium">{activity.auth.has_password ? "Yes" : "No (invite-only)"}</div>
                  </div>
                  <div className="border rounded-md p-3">
                    <div className="text-xs text-muted-foreground">Providers</div>
                    <div className="font-medium">{activity.auth.providers.join(", ") || "—"}</div>
                  </div>
                </div>

                {activity.subscription && (
                  <div className="border rounded-md p-3 space-y-2">
                    <h4 className="text-sm font-semibold flex items-center justify-between">
                      <span>Subscription · {activity.org?.name}</span>
                      <Badge className={
                        activity.subscription.status === "active" ? "bg-green-600" :
                        activity.subscription.status === "trial" || activity.subscription.status === "trialing" ? "bg-blue-600" :
                        activity.subscription.status === "past_due" ? "bg-amber-600" :
                        activity.subscription.status === "canceled" ? "bg-red-600" : ""
                      }>
                        {activity.subscription.status || "unknown"}
                      </Badge>
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-muted-foreground">Plan:</span> {activity.subscription.plan || "—"}</div>
                      <div><span className="text-muted-foreground">Tier:</span> {activity.subscription.tier || "—"}</div>
                      <div><span className="text-muted-foreground">Period:</span> {activity.subscription.period || "—"}</div>
                      <div><span className="text-muted-foreground">Renews:</span> {activity.subscription.current_period_end ? format(new Date(activity.subscription.current_period_end), "MMM d, yyyy") : "—"}</div>
                      <div className="col-span-2"><span className="text-muted-foreground">Stripe Customer:</span> <code className="text-[10px]">{activity.subscription.stripe_customer_id || "none"}</code></div>
                      <div className="col-span-2"><span className="text-muted-foreground">Stripe Subscription:</span> <code className="text-[10px]">{activity.subscription.stripe_subscription_id || "none"}</code></div>
                    </div>
                  </div>
                )}

                {activity.payments && activity.payments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Recent Payments ({activity.payments.length})</h4>
                    <div className="border rounded-md divide-y">
                      {activity.payments.slice(0, 10).map((p: any, i: number) => (
                        <div key={i} className="flex justify-between px-3 py-2 text-sm">
                          <div>
                            <div className="font-medium">${Number(p.amount || 0).toFixed(2)} <span className="text-xs text-muted-foreground">{p.payment_method}</span></div>
                            <div className="text-[11px] text-muted-foreground">{p.payment_date ? format(new Date(p.payment_date), "MMM d, yyyy") : "—"}{p.notes ? ` · ${p.notes}` : ""}</div>
                          </div>
                          <Badge variant={p.status === "completed" || p.status === "succeeded" ? "default" : "outline"}>{p.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activity.subscription_events && activity.subscription_events.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Subscription Events</h4>
                    <div className="border rounded-md divide-y max-h-40 overflow-auto">
                      {activity.subscription_events.map((e: any, i: number) => (
                        <div key={i} className="px-3 py-2 text-xs flex justify-between">
                          <div>
                            <div className="font-medium">{e.event_type}{e.reason ? ` — ${e.reason}` : ""}</div>
                            <div className="text-muted-foreground">{format(new Date(e.created_at), "MMM d, yyyy h:mm a")}</div>
                          </div>
                          {e.mrr_change ? <div className={Number(e.mrr_change) >= 0 ? "text-green-600" : "text-red-600"}>${Number(e.mrr_change).toFixed(2)}</div> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold mb-2">Activity Counts</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(activity.counts).map(([k, v]) => (
                      <div key={k} className="border rounded-md p-2 flex justify-between text-sm">
                        <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="font-semibold">{v as number}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">Recent Timeline ({activity.recent.length})</h4>
                  <ScrollArea className="h-64 border rounded-md">
                    <div className="p-3 space-y-2">
                      {activity.recent.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-8">No recorded activity</div>
                      ) : activity.recent.map((e: any, i: number) => (
                        <div key={i} className="border-b last:border-0 pb-2 last:pb-0">
                          <div className="text-xs text-muted-foreground">{format(new Date(e.created_at), "MMM d, yyyy h:mm a")} · {e.event_type}</div>
                          <div className="text-sm">{e.description}</div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user details and organization assignment</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editFormData.full_name}
                  onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  placeholder="(555) 123-4567" />
              </div>
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select value={editFormData.org_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, org_id: value })}>
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editFormData.org_id && (
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={editFormData.role}
                    onValueChange={(value: any) => setEditFormData({ ...editFormData, role: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="rep">Rep</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveUser} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
